import React, { useState } from 'react';
import { ShoppingBagIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useQuery, useMutation, usePagination, useDebounce } from '../../hooks';
import { ordersAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, StatusBadge, Modal,
  Select, Pagination, LoadingPage, EmptyState, Badge, Tabs,
} from '../../components/common/UI';
import { format } from 'date-fns';


const STATUS_OPTS = ['pending', 'confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled', 'return_requested', 'return_approved', 'pickup_scheduled', 'return_picked', 'refunded'];
const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ── Order detail modal ────────────────────────────────
const OrderDetailModal = ({ orderId, open, onClose, onUpdated }) => {
  const { data: order, loading, refetch } = useQuery(
    () => ordersAPI.detail(orderId),
    [orderId],
    { enabled: !!orderId && open }
  );
  const [newStatus, setNewStatus] = useState('');
  const [note, setNote] = useState('');
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const { mutate: updateStatus, loading: updating } = useMutation(
    () => ordersAPI.updateStatus(orderId, { status: newStatus, note: note || `Status updated to ${newStatus}` }),
    { successMsg: 'Order status updated', onSuccess: () => { refetch(); onUpdated?.(); setNewStatus(''); setNote(''); } }
  );

  const handleItemStatusChange = async (itemId, targetStatus) => {
    if (!targetStatus) return;
    setUpdatingItemId(itemId);
    try {
      await ordersAPI.updateItemStatus(orderId, itemId, {
        status: targetStatus,
        note: `Item status updated to ${targetStatus}`
      });
      toast.success(`Item status → ${targetStatus.replace(/_/g, ' ')}`);
      refetch();
      onUpdated?.();
    } catch (err) {
      // If server error or endpoint not yet migrated, toast error
      const msg = err.response?.data?.msg || err.response?.data?.message || 'Failed to update item status';
      toast.error(msg);
    } finally {
      setUpdatingItemId(null);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Order ${order?.order_number || '…'}`} size="xl">
      {loading ? <LoadingPage /> : order && (
        <div className="space-y-5 overflow-y-auto max-h-[80vh] px-1">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['Order Status', <StatusBadge status={order.status} />],
              ['Total', <span className="font-mono font-bold">₹{Number(order.total_amount).toLocaleString()}</span>],
              ['Payment', <StatusBadge status={order.payment?.status} />],
              ['Method', <Badge variant="gray">{order.payment?.method?.toUpperCase()}</Badge>],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl bg-surface-50 p-3">
                <p className="text-xs text-surface-400 mb-1">{label}</p>
                {val}
              </div>
            ))}
          </div>

          {/* Shipping */}
          <div className="rounded-xl border border-surface-100 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">Shipping Address</p>
            <p className="text-sm font-medium">{order.shipping_address?.full_name}</p>
            <p className="text-sm text-surface-500">{order.shipping_address?.address_line1}, {order.shipping_address?.city}, {order.shipping_address?.state} — {order.shipping_address?.pincode}</p>
            <p className="text-sm text-surface-500">📞 {order.shipping_address?.phone}</p>
          </div>

          {/* ── Items — per-item status badge + dropdown ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-surface-500">Order Items</p>
              <span className="text-xs text-surface-400">{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-2.5">
              {order.items?.map(item => (
                <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-surface-50 p-3 border border-surface-100/60">
                  {/* Left: image + name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-white">
                      {item.product_image ? (
                        <img src={item.product_image} alt={item.product_name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-surface-100 text-surface-400">
                          <ShoppingBagIcon className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-surface-900 truncate">{item.product_name}</p>
                      <div className="flex items-center gap-2 text-xs text-surface-400 mt-0.5">
                        <span>{item.product_sku || 'SKU'} × {item.quantity}</span>
                        {item.variant_name && <Badge variant="gray" size="xs">{item.variant_name}</Badge>}
                      </div>
                    </div>
                  </div>

                  {/* Right: status badge + dropdown + price */}
                  <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-surface-100">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={item.status || order.status} />
                      <select
                        className="text-xs py-1 px-2 rounded-lg border border-surface-200 bg-white text-surface-700 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer disabled:opacity-50"
                        value={item.status || order.status || ''}
                        onChange={(e) => handleItemStatusChange(item.id, e.target.value)}
                        disabled={updatingItemId === item.id}
                        title="Change Item Status"
                      >
                        {STATUS_OPTS.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                      {updatingItemId === item.id && (
                        <span className="text-[10px] text-brand-500 animate-pulse">Saving…</span>
                      )}
                    </div>
                    <span className="font-mono font-semibold text-sm">₹{Number(item.total_price).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="space-y-1 text-sm">
            {[
              ['Subtotal', order.subtotal],
              ['Discount', `-${order.discount_amount}`],
              ['Shipping', order.shipping_amount],
              ['Tax', order.tax_amount],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between text-surface-600">
                <span>{l}</span><span className="font-mono">₹{Number(v).toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold text-surface-900 border-t border-surface-100 pt-1">
              <span>Total</span><span className="font-mono">₹{Number(order.total_amount).toLocaleString()}</span>
            </div>
          </div>

          {/* Status history */}
          {order.statusHistory?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">Status History</p>
              <div className="space-y-1">
                {order.statusHistory.map(h => (
                  <div key={h.id} className="flex items-center gap-2 text-xs text-surface-500">
                    <span className="font-mono">{h.created_at ? format(new Date(h.created_at), 'dd MMM HH:mm') : ''}</span>
                    <span className="text-surface-300">→</span>
                    <StatusBadge status={h.to_status} />
                    {h.note && <span className="text-surface-400">— {h.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Update entire Order status */}
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-700">Update Order Status</p>
            <div className="grid grid-cols-2 gap-3">
              <Select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                <option value="">— Select new status —</option>
                {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </Select>
              <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" />
            </div>
            <Button variant="primary" size="sm" disabled={!newStatus} loading={updating} onClick={updateStatus}>
              Update Order Status
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ── Main page ─────────────────────────────────────────
export default function OrdersPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const { page, setPage, reset } = usePagination();
  const dSearch = useDebounce(search);

  const { data, loading, refetch } = useQuery(
    () => ordersAPI.adminList({ page, limit: 20, status: statusFilter || undefined, search: dSearch || undefined }),
    [page, statusFilter, dSearch]
  );

  const orders = Array.isArray(data)
    ? data
    : (data?.rows || data?.data?.rows || (Array.isArray(data?.data) ? data.data : Array.isArray(data?.orders) ? data.orders : []));
  const total = data?.count ?? data?.total ?? data?.pagination?.total ?? data?.data?.count ?? (Array.isArray(orders) ? orders.length : 0);
  const pages = (data?.pagination?.pages ?? Math.ceil(total / 20)) || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Orders" subtitle={`${total} total orders`} />

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="w-full overflow-x-auto">
            <Tabs tabs={STATUS_TABS} active={statusFilter} onChange={v => { setStatusFilter(v); reset(); }} />
          </div>
          <SearchInput value={search} onChange={v => { setSearch(v); reset(); }} placeholder="Search order number…" className="flex-1 min-w-[200px]" />
        </div>

        {loading ? <LoadingPage /> : orders.length === 0 ? (
          <EmptyState icon={ShoppingBagIcon} title="No orders found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Order #</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>
                      <button
                        onClick={() => setSelectedId(o.id)}
                        className="font-mono text-sm font-semibold text-brand-700 hover:underline"
                      >
                        {o.order_number}
                      </button>
                    </td>
                    <td>
                      <div>
                        <p className="font-medium text-sm">{o.shipping_address?.full_name || `${o.customer?.first_name || ''} ${o.customer?.last_name || ''}`.trim() || 'Unknown'}</p>
                        <p className="text-xs text-surface-400">{o.customer?.email}</p>
                      </div>
                    </td>
                    <td><span className="text-sm text-surface-600">{o.items?.length} item{o.items?.length !== 1 ? 's' : ''}</span></td>
                    <td><span className="font-mono font-semibold">₹{Number(o.total_amount).toLocaleString()}</span></td>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        <Badge variant="gray">{o.payment?.method?.toUpperCase() || '—'}</Badge>
                        <StatusBadge status={o.payment?.status} />
                      </div>
                    </td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="text-surface-400 text-xs">{o.created_at ? format(new Date(o.created_at), 'dd MMM yy') : '—'}</td>
                    <td>
                      <button className="btn btn-ghost p-1.5 rounded-lg" onClick={() => setSelectedId(o.id)} title="View Order">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <OrderDetailModal
        orderId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={refetch}
      />
    </div>
  );
}
